import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { bankAccountsApi, banksApi } from '../../services/adminApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ account, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: account.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tableCellStyle = {
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb'
  };

  const statusButtonStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
    color: isActive ? '#166534' : '#dc2626',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s'
  });

  const actionLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: '0.75rem',
    transition: 'color 0.2s'
  };

  const actionButtonStyle = {
    color: '#dc2626',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'color 0.2s'
  };

  const dragHandleStyle = {
    cursor: 'grab',
    padding: '0.5rem',
    color: '#6b7280',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.25rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    transition: 'all 0.2s'
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            {...listeners}
            style={dragHandleStyle} 
            title="Sürükleyerek sıralayın"
          >
            ⋮⋮
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#111827' }}>
              {account.bank_name}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {account.account_holder}
            </div>
          </div>
        </div>
      </td>
      <td style={tableCellStyle}>
        <span style={{
          backgroundColor: '#e0f2fe',
          color: '#0369a1',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.375rem',
          fontSize: '0.75rem',
          fontWeight: '600'
        }}>
          {account.currency}
        </span>
      </td>
      <td style={{ ...tableCellStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {account.iban}
      </td>
      <td style={tableCellStyle}>
        <span style={statusButtonStyle(account.is_active)}>
          {account.is_active ? 'Aktif' : 'Pasif'}
        </span>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {new Date(account.created_at).toLocaleDateString('tr-TR')}
        </div>
      </td>
      <td style={tableCellStyle}>
        <Link
          to={`/admin/bank-accounts/${account.id}/edit`}
          style={actionLinkStyle}
          onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
          onMouseOut={(e) => e.target.style.color = '#2563eb'}
        >
          Düzenle
        </Link>
        <button
          onClick={() => onDelete(account.id)}
          style={actionButtonStyle}
          onMouseOver={(e) => e.target.style.color = '#b91c1c'}
          onMouseOut={(e) => e.target.style.color = '#dc2626'}
        >
          Sil
        </button>
      </td>
    </tr>
  );
};

const BankAccountsList = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await bankAccountsApi.getAll();
      
      if (response.data.success) {
        const accountsData = response.data.data?.data || response.data.data || [];
        setAccounts(accountsData);
        setError(null);
      } else {
        setError('Banka hesapları yüklenemedi');
      }
    } catch (err) {
      console.error('Accounts fetch error:', err);
      setError('Banka hesapları yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu banka hesabını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await bankAccountsApi.delete(id);
      if (response.data.success) {
        await fetchAccounts();
        alert('Banka hesabı başarıyla silindi');
      } else {
        alert('Banka hesabı silinemedi');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Banka hesabı silinirken hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = accounts.findIndex(item => item.id === active.id);
      const newIndex = accounts.findIndex(item => item.id === over.id);
      
      const newAccounts = arrayMove(accounts, oldIndex, newIndex);
      setAccounts(newAccounts);

      try {
        // Group accounts by bank_id and update sort_order for each bank
        const bankGroups = {};
        newAccounts.forEach((account, index) => {
          if (!bankGroups[account.bank_id]) {
            bankGroups[account.bank_id] = [];
          }
          bankGroups[account.bank_id].push({ ...account, newIndex: index });
        });

        // Update sort_order for each bank
        const updatePromises = Object.keys(bankGroups).map(bankId => {
          const bankAccounts = bankGroups[bankId];
          const sortOrder = bankAccounts[0].newIndex + 1; // Use first account's position as bank sort order
          return banksApi.update(bankId, { sort_order: sortOrder });
        });

        await Promise.all(updatePromises);
        
        // Update local state with new sort_order values
        setAccounts(prevAccounts => 
          prevAccounts.map((account, index) => ({
            ...account,
            sort_order: index + 1
          }))
        );
      } catch (error) {
        console.error('Error updating sort order:', error);
        // Revert on error
        await fetchAccounts();
      }
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const buttonStyle = {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'background-color 0.2s'
  };

  const tableStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    width: '100%',
    borderCollapse: 'collapse'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f9fafb',
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const loadingStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const errorStyle = {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  };

  const errorTextStyle = {
    color: '#dc2626',
    marginBottom: '1rem'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Banka hesapları yükleniyor...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          <p style={errorTextStyle}>{error}</p>
          <button
            onClick={fetchAccounts}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Banka Hesapları - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Banka Hesapları</h1>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/admin/banks" style={buttonStyle}>
              Bankalar
            </Link>
            <Link to="/admin/bank-accounts/create" style={buttonStyle}>
              Yeni Hesap Ekle
            </Link>
          </div>
        </div>

        {accounts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Henüz banka hesabı bulunmamaktadır.
          </div>
        ) : (
          <div style={tableStyle}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Banka Bilgileri</th>
                    <th style={tableHeaderStyle}>Para Birimi</th>
                    <th style={tableHeaderStyle}>IBAN</th>
                    <th style={tableHeaderStyle}>Durum</th>
                    <th style={tableHeaderStyle}>Oluşturulma Tarihi</th>
                    <th style={tableHeaderStyle}>İşlemler</th>
                  </tr>
                </thead>
                <SortableContext
                  items={accounts.map(account => account.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {accounts.map((account) => (
                      <SortableItem
                        key={account.id}
                        account={account}
                        onEdit={() => {}}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
          </div>
        )}
      </div>
    </>
  );
};

export default BankAccountsList;